import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    input,
    Input,
    OnDestroy,
    OnInit,
    Self,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormBuilder,
    FormControl,
    FormsModule,
    NgControl,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {AppValidators, TranslatePipe} from '@brunata-angular/util';
import {v4 as uuidv4} from 'uuid';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DateTime} from 'luxon';
import {ControlMessagesComponent} from '../control-messages/control-messages.component';
import {InputDateGroupComponent} from '../input-date-group/input-date-group.component';
import {patchControlMethod} from '../patch-control-method';

export type DateRange = {startDate: DateTime | null; endDate: DateTime | null};

@Component({
    selector: 'app-input-date-range-picker',
    templateUrl: './input-date-range-picker.component.html',
    styleUrl: './input-date-range-picker.component.scss',
    imports: [
        ReactiveFormsModule,
        FormsModule,
        NgbModule,
        ControlMessagesComponent,
        TranslatePipe,
        InputDateGroupComponent,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputDateRangePickerComponent implements OnInit, OnDestroy, ControlValueAccessor {
    @Input() required = false;
    @Input() id: string = uuidv4();

    readonly layout = input<'vertical' | 'horizontal'>('horizontal');
    readonly outsideDays = input<'visible' | 'hidden' | 'collapsed'>();
    readonly minDate = input<DateTime | null>(null);
    readonly maxDate = input<DateTime | null>(null);

    formGroup = this.formBuilder.group(
        {
            startDate: new FormControl<DateTime | null>(null, {
                validators: this.required ? Validators.required : null,
            }),
            endDate: new FormControl<DateTime | null>(null, {
                validators: this.required ? Validators.required : null,
            }),
        },
        {
            validators: [AppValidators.dateIsFirst],
        },
    );

    private onTouch: (() => void) | undefined;
    private onChange: ((value: DateRange) => void) | undefined;

    private restoreMarkAsTouched: (() => void) | undefined;

    constructor(
        @Self() public ngControl: NgControl,
        private changeDetectorRef: ChangeDetectorRef,
        private formBuilder: FormBuilder,
    ) {
        ngControl.valueAccessor = this;
        this.formGroup.valueChanges.pipe(takeUntilDestroyed()).subscribe(value => {
            if (this.formGroup.invalid) {
                return;
            }
            if (this.onChange == null) {
                return;
            }

            this.onChange({
                startDate: value.startDate ?? null,
                endDate: value.endDate ?? null,
            });
        });
    }

    ngOnInit(): void {
        this.ngControl.control?.addValidators([this.boundValidate]);

        /*  Then using ChangeDetectionStrategy.OnPush the component doesn't notice, when the control is marked as
         touched, and will therefore not show validation errors. However, Angular doesn't offer a simple way of
         listening to, when a control is marked as touched, that we can hook into. So we have to reassign the
         markAsTouched() method in order to call detectChanges(). */
        const control = this.ngControl.control;
        if (control != null) {
            this.restoreMarkAsTouched = patchControlMethod(control, 'markAsTouched', () =>
                this.changeDetectorRef.detectChanges(),
            );
        }
    }

    /** Angular's `cleanUpControl` does not remove the validator added above. (AGI-19494) */
    ngOnDestroy(): void {
        this.restoreMarkAsTouched?.();

        const control = this.ngControl.control;
        if (control == null) {
            return;
        }
        control.removeValidators(this.boundValidate);
        control.updateValueAndValidity({emitEvent: false});
    }
    onBlur(): void {
        if (this.onTouch != null) {
            this.onTouch();
        }
    }

    registerOnChange(fn: (value: DateRange) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    writeValue(value: DateRange): void {
        this.formGroup.patchValue(
            {startDate: value.startDate, endDate: value.endDate},
            {emitEvent: false},
        );
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.formGroup.disable();
        } else {
            this.formGroup.enable();
        }
    }

    validate(_: AbstractControl): ValidationErrors | null {
        return this.formGroup.errors;
    }

    /** One stable reference: `removeValidators` matches by identity. */
    private readonly boundValidate = (control: AbstractControl): ValidationErrors | null =>
        this.validate(control);
}
